import React, { useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import { getConfig, getLiveUsers, postConfig } from '../api/cluster-manager-config'
import { SuperClusterConfigOptions } from '../types'
import authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import timestampToDateString from '../utils'

export default function Config() {
    const [config, setConfig] = useState<SuperClusterConfigOptions>()
    const [formState, setFormState] = useState<SuperClusterConfigOptions>()
    const [username, setUsername] = useState<string>()
    useEffect(() => {
        ;(async () => {
            const fetchedConfig = await getConfig()
            if (fetchedConfig?.success) {
                const user = await getLiveUsers(fetchedConfig.data.updatedBy)
                if (user?.id) setUsername(user.name)
                setConfig(fetchedConfig?.data)
                setFormState(fetchedConfig?.data)
            }
        })()
    }, [])

    const handlePost = useCallback(async (parameters: SuperClusterConfigOptions) => {
        const response = await postConfig({ ...parameters, updatedAt: null, updatedBy: Number(authorization.user.id) })
        if (response?.success) {
            setConfig(response.data)
            setFormState(response?.data)
            globalNotification.success('Configuração salva com sucesso')
        } else globalNotification.error('Erro ao salvar a configuração')
    }, [])

    return (
        <Container>
            <div className="title-bar">
                <span>DADOS ESTÁTICOS</span>
                <span>Gerenciador de clusters (Visualização de mapas)</span>
            </div>
            <div className="cluster-manager-form-container">
                <div className="cluster-manager-forms">
                    <div className="cluster-manager-fieldset-container">
                        <span className="cluster-manager-fieldset-title">Parâmetros de visualização</span>
                        <div className="cluster-manager-fieldset">
                            <div className="cluster-manager-input-container">
                                <span className="label">minZoom</span>
                                <input
                                    type="number"
                                    placeholder="0 (default)"
                                    min={0}
                                    className="no-arrow-number-input"
                                    value={formState?.minZoom}
                                    onChange={e => setFormState({ ...formState, minZoom: e.target.value })}
                                />
                                <span className="info">
                                    <i className="far fa-exclamation-circle" /> Nível mínimo de zoom que os clusters
                                    serão gerados
                                </span>
                            </div>
                            <div className="cluster-manager-input-container">
                                <span className="label">maxZoom</span>
                                <input
                                    type="number"
                                    placeholder="20 (default)"
                                    className="no-arrow-number-input"
                                    min={2}
                                    onChange={e => setFormState({ ...formState, maxZoom: e.target.value })}
                                    value={formState?.maxZoom}
                                />
                                <span className="info">
                                    <i className="far fa-exclamation-circle" /> Nível máximo de zoom que os clusters
                                    serão gerados
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="cluster-manager-fieldset-container">
                        <span className="cluster-manager-fieldset-title">Pontos a clusterizar</span>
                        <div className="cluster-manager-fieldset">
                            <div className="cluster-manager-input-container">
                                <span className="label">minPoints</span>
                                <input
                                    type="number"
                                    placeholder="2 (default)"
                                    className="no-arrow-number-input"
                                    onChange={e => setFormState({ ...formState, minPoints: e.target.value })}
                                    value={formState?.minPoints}
                                />
                                <span className="info">
                                    <i className="far fa-exclamation-circle" /> Número mínimo de pontos que compõem um
                                    cluster
                                </span>
                            </div>
                            <div className="cluster-manager-input-container">
                                <span className="label">radius</span>
                                <input
                                    type="number"
                                    className="no-arrow-number-input"
                                    onChange={e => setFormState({ ...formState, radius: e.target.value })}
                                    placeholder="40px (default)"
                                    value={formState?.radius}
                                />
                                <span className="info">
                                    <i className="far fa-exclamation-circle" /> Raio do cluster em pixels
                                </span>
                            </div>
                            <div className="cluster-manager-input-container">
                                <span className="label">extent</span>
                                <input
                                    type="number"
                                    className="no-arrow-number-input"
                                    onChange={e => setFormState({ ...formState, extent: e.target.value })}
                                    placeholder="512 (default)"
                                    value={formState?.extent}
                                />
                                <span className="info">
                                    <i className="far fa-exclamation-circle" /> (Tiles) Extensão do "Tile". O raio é
                                    calculado em relação a esse valor
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="cluster-manager-last-edit">
                    Última edição:{' '}
                    <span>{config?.updatedAt > 0 ? timestampToDateString(config.updatedAt, true) : '--/--/--'}</span> -
                    por <span>{username}</span>
                </div>
                <div className="sub-cancel-container">
                    <SubmitOrCancel
                        disabled={false}
                        onCancel={() => setFormState(config)}
                        onSubmit={async () => {
                            await handlePost(formState)
                        }}
                        buttonMessage={'Aplicar'}
                        type="button"
                    />
                </div>
            </div>
        </Container>
    )
}

const Container = styled.div`
    background-color: ${({ theme }) => theme['gray-0']};
    height: calc(100% - 36px);
    width: 100%;
    position: relative;
    top: 36px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: center;
    .title-bar {
        width: 100%;
        height: 36px;
        background-color: ${({ theme }) => theme['gray-200']};
        color: ${({ theme }) => theme['gray-700']};
        font-family: ${({ theme }) => theme['font-semibold']};
        padding-left: 24px;
        display: flex;
        align-items: center;
        gap: 10px;

        span:last-child {
            border-left: ${({ theme }) => `1px solid ${theme['gray-400']}`};
            padding-left: 10px;
        }
    }

    .cluster-manager-form-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;

        .cluster-manager-forms {
            display: flex;
            gap: 24px;
            .cluster-manager-fieldset-container {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .cluster-manager-fieldset-title {
                    font-size: 14px;
                    color: ${({ theme }) => theme['gray-900']};
                    font-family: ${({ theme }) => theme['font-semibold']};
                }

                .cluster-manager-fieldset {
                    display: flex;
                    flex-direction: column;
                    background-color: ${({ theme }) => theme['gray-0']};
                    min-width: 400px;
                    padding: 24px;
                    border: ${({ theme }) => `1px solid ${theme['gray-500']}`};
                    border-radius: 3px;
                    gap: 16px;

                    .cluster-manager-input-container {
                        display: flex;
                        flex-direction: column;
                        line-height: 16px;
                        gap: 4px;

                        .label {
                            color: ${({ theme }) => theme['gray-900']};
                            font-family: ${({ theme }) => theme['font-semibold']};
                            font-size: 13px;
                        }

                        .info {
                            color: ${({ theme }) => theme['gray-700']};

                            i {
                                font-size: 10px;
                            }

                            font-size: 12px;
                        }
                    }
                }
            }
        }

        .cluster-manager-last-edit {
            width: 100%;
            font-size: 13px;
            font-family: ${({ theme }) => theme['font-regular']};
            color: ${({ theme }) => theme['gray-600']};
        }

        .sub-cancel-container {
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: space-between;
            height: 63px;
            background-color: ${({ theme }) => theme['gray-200']};
            padding-left: 24px;
        }
    }

    .no-arrow-number-input::-webkit-outer-spin-button,
    .no-arrow-number-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .no-arrow-number-input[type='number'] {
        -moz-appearance: textfield;
    }
`
